import {Body2} from '@ui/atoms/typography';
import {Flex} from '@ui/style/styles';
import {HorizontalSpacer} from '@ui/atoms/spacer';
import styled from 'styled-components';
import {DropdownCustom} from '@ui/molecules/dropdown-custom';
import {DropdownWrapper} from '../../../../../you/you-page.styles';
import {Button} from '@ui/atoms/button';
import {GoalActivity} from './goal-activity';
import {useEffect, useMemo, useState} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import {DropdownItem} from '../../../../../you/you-page.styles';
import {IGoalResult} from '@hooks';
import {GoalProgressChart} from './goal-progress-chart';
import {GoalProgress, metricProgress} from '@utils/proportions';
import {getColorFromTheme} from '@ui/ui-utils';

const GraphContainerWrapper = styled.div`
  margin-top: 20px;

  .heading {
    padding: 16px 0 8px;
  }

  @media (max-width: 800px) {
    .heading {
      padding: 16px 0 8px;
      justify-content: space-between;
    }
    padding: 0px;
    border-bottom: 0px;
  }
`;

const CustomButton = styled(Button)`
  min-width: 120px;
  max-width: 280px;
  height: 38px;
  padding: 0px 8px 0px 16px;
  display: flex;
  align-items: center;
  z-index: 0;
  overflow: hidden;
`;

const SelectedTrend = styled(Body2)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrendDropdownItem = styled(DropdownItem)`
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
`;

const GraphDiv = styled.div`
  width: 100%;
  margin-top: 8px;

  @media (max: width: 1240px) {
    width: 100%;
  }

  @media (max-width: 800px) {
    border-top: 1px solid ${getColorFromTheme('borderLight')};
    width: 100%;
    padding: 0px;
  }
`;
const GraphActivity = ({
  goal,
  krValue,
  krStartValue,
  isKeyResult,
  data,
  currentProgress,
}: any) => {
  const startValue = isKeyResult ? krStartValue : goal?.startValue;
  const targetValue = isKeyResult ? krValue : goal?.targetValue;

  return (
    <>
      <GraphDiv>
        <GoalProgressChart
          activities={data}
          currentProgress={Number(currentProgress) || 0}
          startDate={goal.startDate}
          endDate={goal.endDate}
          startValue={startValue}
          targetValue={targetValue}
        />
      </GraphDiv>
    </>
  );
};

const OverflowWrapper = styled.div`
  margin-right: 8px;
  @media (max-width: 800px) {
    overflow-x: scroll;
    overflow-y: hidden;

    &::-webkit-scrollbar {
      width: 2em;
      border-top: 1px solid ${getColorFromTheme('borderLight')};
      height: 2.1em;
    }

    &::-webkit-scrollbar-track {
      background: ${getColorFromTheme('backgroundDark')};
      border: 0.8em solid rgba(0, 0, 0, 0);
      background-clip: padding-box;

      -webkit-border-radius: 1.5em;

      border-radius: 100px;
    }

    &::-webkit-scrollbar-thumb {
      height: 10em;
      border: 0.8em solid rgba(0, 0, 0, 0);
      background: ${getColorFromTheme('borderDark')};
      border-radius: 10px;
      background-clip: padding-box;
      -webkit-border-radius: 1.5em;

      -webkit-box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.025);
    }

    &::-webkit-scrollbar-button {
      width: 0;
      height: 0;
      display: none;
    }

    &::-webkit-scrollbar-corner {
      background-color: transparent;
    }
  }
`;

const GraphContainer = ({
  goal,
  setSelectedValue: updateSelectedValue,
}: {
  setSelectedValue: any;
  goal: IGoalResult;
}) => {
  const [selectedKeyResult, setSelectedKeyResult] = useState('');

  const [selectedValue, setSelectedValue] = useState('Overall goal');

  const [krValue, setKrValue] = useState(0);
  const [krStartValue, setKrStartValue] = useState<number>();

  useEffect(() => {
    setSelectedKeyResult('');
    setSelectedValue('Overall goal');
    setKrValue(0);
    setKrStartValue(undefined);
    updateSelectedValue({id: '', name: 'All activity'});
  }, [goal.id, goal.name, updateSelectedValue]);

  const activities = useMemo(() => {
    if (selectedKeyResult) {
      return goal.keyResults.find(
        (keyResult) => keyResult.id === selectedKeyResult,
      )?.activities;
    }
    return goal.activities;
  }, [goal.activities, goal.keyResults, selectedKeyResult]);
  const selectedResult: any = goal.keyResults.find(
    (keyResult) => keyResult.id === selectedKeyResult,
  );
  const currentProgress = selectedResult
    ? metricProgress(
        selectedResult.startValue,
        selectedResult.currentValue,
        selectedResult.targetValue,
      )
    : GoalProgress(goal).percentage;

  return (
    <GraphContainerWrapper>
      {!goal?.isKpi && (
        <Flex alignItems="center" className="heading mt-2">
          <Body2 weight="bold">Showing trend for</Body2>
          <HorizontalSpacer size="10px" />
          <DropdownWrapper>
            <DropdownCustom
              collapseOnClick={true}
              menu={(handleClose: () => void) => (
                <div style={{width: '280px'}}>
                  <TrendDropdownItem
                    title="Overall goal"
                    onClick={() => {
                      setSelectedKeyResult('');
                      setKrValue(0);
                      setKrStartValue(undefined);

                      handleClose();

                      updateSelectedValue({
                        id: '',
                        name: 'All activity',
                      });

                      setSelectedValue('Overall goal');
                    }}>
                    Overall goal
                  </TrendDropdownItem>

                  {goal.keyResults.map((keyResult: any) => (
                    <TrendDropdownItem
                      key={keyResult.id}
                      title={keyResult.name}
                      onClick={() => {
                        setSelectedKeyResult(keyResult.id);
                        handleClose();

                        updateSelectedValue({
                          id: keyResult.id,
                          name: keyResult.name,
                        });
                        setKrValue(keyResult.targetValue);
                        setKrStartValue(keyResult.startValue);
                        setSelectedValue(keyResult.name);
                      }}>
                      {keyResult.name}
                    </TrendDropdownItem>
                  ))}
                </div>
              )}
              customComponent={(
                handleOpen: (event: any) => void,
                open: boolean,
              ) => (
                <>
                  <CustomButton kind="secondary" onClick={handleOpen}>
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      style={{width: '100%'}}>
                      <SelectedTrend weight="bold" title={selectedValue}>
                        {selectedValue}
                      </SelectedTrend>
                      <FiChevronDown
                        style={{
                          flexShrink: 0,
                          margin: '0 5px 0 16px',
                        }}
                        aria-hidden="true"
                      />
                    </Flex>
                  </CustomButton>
                </>
              )}
              customDropdownWrapperStyles={{
                boxSizing: 'border-box',
                left: 0,
                right: 'auto',
                top: 'calc(100% + 8px)',
                width: '280px',
              }}
            />
          </DropdownWrapper>
        </Flex>
      )}

      <OverflowWrapper>
        <GraphActivity
          goal={goal}
          isKeyResult={!!selectedKeyResult}
          data={activities}
          krValue={krValue}
          krStartValue={krStartValue}
          currentProgress={currentProgress}
        />
      </OverflowWrapper>
    </GraphContainerWrapper>
  );
};

const GraphWrapper = ({
  goal,
  setSelectedValue,
}: {
  goal: IGoalResult;
  setSelectedValue: any;
}) => {
  return <GraphContainer goal={goal} setSelectedValue={setSelectedValue} />;
};

export const InsightTab = ({goal}: {goal: IGoalResult}) => {
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: `All activity`,
  });

  return (
    <>
      <GraphWrapper goal={goal} setSelectedValue={setSelectedValue} />

      <GoalActivity
        id={goal.id}
        createdBy={goal.createdBy}
        selectedValue={selectedValue}
        isGoalKpi={goal.isKpi || false}
        updatedAt={goal.createdAt}
      />
    </>
  );
};
